import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";

const sections = [
  {
    title: "Who We Are",
    paragraphs: [
      "We’re Ted Vernon and Ivory Johnson, co-founders of Relocation Clarity.",
      "Ted's career as a business analyst, information architect, and technical writer was built around a single goal: helping people understand complex information and make better decisions. Relocation Clarity applies that same approach to one of life's biggest decisions—choosing where to build your next chapter.",
      "Ivory is a Certified Financial Planner™ professional whose career has focused on helping people navigate important life decisions by balancing priorities, evaluating options, and creating practical plans for the future. That perspective naturally influenced how Relocation Clarity approaches relocation decisions—looking beyond emotion to compare options thoughtfully, understand tradeoffs, and focus on long-term fit rather than short-term excitement.",
      "Together, those complementary backgrounds shaped more than the product. They shaped the philosophy behind it: helping people make one of life's biggest decisions with greater clarity, confidence, and perspective.",
    ],
  },
  {
    title: "How It Started",
    paragraphs: [
      "The idea for Relocation Clarity didn't begin in a conference room or as part of a business plan.",
      "It started during a golf trip.",
      "Like many conversations between friends, we began talking about retirement, travel, and what life might look like if we chose to live abroad someday. As we compared countries, researched options, and shared what we were finding, one thing became obvious.",
      "There was no shortage of information.",
      "There was a shortage of clarity.",
      "Every website had opinions. Every video reflected one person's experience. Every ranking claimed to identify the “best” country. But there wasn't a practical, unbiased way to compare countries based on what mattered most to us as individuals.",
      "Somewhere during that conversation, we asked a simple question:",
      "\"What if we built the tool we wish we had?\"",
      "The answer became Relocation Clarity.",
    ],
  },
  {
    title: "Why We Built It",
    paragraphs: [
      "Our goal has never been to tell people where they should move. Our goal is to help them make a better decision.",
      "Relocation Clarity brings together the factors that matter most—budget, healthcare, residency pathways, lifestyle, climate, safety, and personal priorities—and explains why certain countries may fit better than others based on each person's unique situation.",
      "Because the best country isn't the same for everyone. The right country is the one that's right for your life.",
    ],
  },
  {
    title: "Independent by Design",
    paragraphs: [
      "Relocation Clarity doesn't promote countries, sell real estate, or recommend destinations based on sponsorships or commissions.",
      "Our responsibility is to you—not to a country, agency, or advertiser.",
      "That means you'll find honest tradeoffs, practical guidance, and recommendations designed to help you make the decision that's right for your life—not ours.",
    ],
  },
  {
    title: "Our Commitment",
    paragraphs: [
      "Our commitment is simple.",
      "Provide independent guidance.",
      "Explain the tradeoffs honestly.",
      "Help people make one of life's biggest decisions with greater clarity.",
    ],
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="about-page">
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-3xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="about-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="about-hero-accent" />
            <p className="rc-section-kicker mb-4" data-testid="about-hero-kicker">About</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="about-page-title">
              About Relocation Clarity
            </h1>
            <p className="mt-6 max-w-[42rem] text-lg leading-8 text-stone-800 sm:text-[1.35rem] sm:leading-9" data-testid="about-page-subtitle">
              Helping You Make One of Life&apos;s Biggest Decisions with {"Greater\u00A0Clarity"}
            </p>
            <div className="mt-6 max-w-2xl space-y-5 text-base leading-8 text-stone-600" data-testid="about-page-intro">
              <p>
                Choosing where to live—and where to build your next chapter—is one of the biggest decisions you&apos;ll ever make. Yet most people are left sorting through country videos, Facebook groups, rankings, and conflicting opinions, trying to figure out what actually applies to their own lives. We created Relocation Clarity because we believed there had to be a better way.
              </p>
              <p>
                Relocation Clarity isn&apos;t really about relocation. It&apos;s about helping people make a complex life decision with greater clarity and confidence. Our goal has never been to tell people where they should move. Our goal is to provide a thoughtful, independent framework that helps people understand the tradeoffs, compare options, and make decisions they can feel confident about.
              </p>
            </div>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="about-story-section">
            <div className="border-t border-stone-200/80 pt-10" data-testid="about-horizontal-rule-block">
              <div className="max-w-2xl space-y-10">
                {sections.map((section, index) => (
                  <section key={section.title} className="space-y-5" data-testid={`about-section-${index + 1}`}>
                    <h2 className="font-manrope text-2xl text-stone-900" data-testid={`about-section-title-${index + 1}`}>
                      {section.title}
                    </h2>
                    <div className="space-y-4 text-base leading-8 text-stone-600" data-testid={`about-section-copy-${index + 1}`}>
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  </section>
                ))}

                <p className="text-base leading-8 text-stone-600" data-testid="about-closing-note">
                  Thank you for visiting Relocation Clarity.
                </p>
                <p className="text-base leading-8 text-stone-600">
                  We hope it helps you move forward with greater confidence, a clearer understanding of your options, and a decision you can feel good about.
                </p>

                <div className="space-y-1 pt-2" data-testid="about-signature-block">
                  <p className="font-manrope text-lg text-stone-900" data-testid="about-signature-name">Ted Vernon &amp; Ivory Johnson</p>
                  <p className="text-sm leading-7 text-stone-500" data-testid="about-signature-title">Co-Founders, Relocation Clarity</p>
                  <p className="pt-2 text-base leading-8 text-stone-600" data-testid="about-signature-note">
                    When we&apos;re not thinking about how to make relocation decisions a little clearer, there&apos;s a good chance you&apos;ll find us on a golf course trying to solve a much more difficult problem—our golf swings.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Where to go next"
            title="If you’re getting to know Relocation Clarity, these are the most useful next pages."
            description="Start with the decision process if you want to understand how the guidance works, preview the Blueprint if you want to see the paid experience, or begin the assessment when you are ready to personalize the answer."
            testIdPrefix="about-next-steps"
            items={[
              {
                slug: "how-it-works",
                eyebrow: "Understand the process",
                title: "See how Relocation Clarity works",
                description: "Understand how the assessment, results, and Blueprint fit together before you start.",
                ctaLabel: "View the process",
                to: "/how-relocation-clarity-works",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the output",
                title: "Preview a Sample Blueprint",
                description: "See how a shortlist, tradeoffs, and next steps are organized in the paid experience.",
                ctaLabel: "Preview the Blueprint",
                to: "/sample-plan",
              },
              {
                slug: "assessment",
                eyebrow: "Personalize it",
                title: "Start your assessment",
                description: "Turn broad country interest into a more structured shortlist based on your life and priorities.",
                ctaLabel: "Begin the assessment",
                to: "/assessment",
              },
            ]}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}